import React from 'react';
import { Document, Page, Text, View, StyleSheet, PDFDownloadLink, Image } from '@react-pdf/renderer';
import { useTranslation } from 'react-i18next';
// Using the new FluturoLire logo
import FluturoLireLogoNewBase64 from '../../assets/img/FluturoLireLogoNewBase64';

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontFamily: 'Times-Roman',
    backgroundColor: '#ffffff',
    fontSize: 10,
  },

  headerSection: {
    marginBottom: 25,
    borderBottom: '2px solid #003663',
    paddingBottom: 15,
  },
  
  companyInfo: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  
  companyLeft: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 15,
  },
  
  logo: {
    width: 110,
    height: 45,
  },
  
  companyTextInfo: {
    flexDirection: 'column',
  },
  
  companyName: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#003663',
    marginBottom: 4,
  },
  
  companySubtitle: {
    fontSize: 10,
    color: '#666666',
    fontStyle: 'italic',
  },
  
  voucherRight: {
    flexDirection: 'column',
    alignItems: 'flex-end',
  },
  
  voucherTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#003663',
    textAlign: 'right',
    marginBottom: 4,
  },
  
  issueDate: {
    fontSize: 9,
    color: '#666666',
    textAlign: 'right',
    marginBottom: 2,
  },
  
  bookingNumber: {
    fontSize: 9,
    color: '#666666',
    textAlign: 'right',
  },
  
  bookingSection: {
    border: '1px solid #003663',
    padding: 14,
    marginBottom: 18,
    borderRadius: 2,
  },
  bookingBlockTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#003663',
    marginBottom: 4,
    textTransform: 'uppercase',
  },
  bookingBlockSub: {
    fontSize: 9,
    fontStyle: 'italic',
    color: '#666666',
    marginBottom: 6,
  },
  
  hotelName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#003663',
  },
  
  // Information tables
  infoTable: {
    marginBottom: 18,
    border: '1px solid #003663',
  },
  
  tableTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#003663',
    backgroundColor: '#ffffff',
    position: 'absolute',
    top: -10,
    left: 12,
    paddingHorizontal: 4,
  },
  
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 5,
    borderBottom: '1px solid #f0f0f0',
  },
  
  tableHeader: {
    backgroundColor: '#e5e6e6',
    paddingVertical: 6,
  },
  
  tableHeaderText: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#003663',
  },
  
  tableCell: {
    flex: 1,
    fontSize: 10,
    color: '#333333',
    paddingHorizontal: 6,
  },
  
  tableCellBold: {
    flex: 1,
    fontSize: 10,
    fontWeight: 'bold',
    color: '#003663',
    paddingHorizontal: 6,
  },
  
  // Guest information
  guestSection: {
    marginBottom: 18,
    border: '1px solid #003663',
  },
  
  guestRow: {
    flexDirection: 'row',
    paddingVertical: 3,
    borderBottom: '1px solid #f0f0f0',
  },
  
  guestCell: {
    flex: 1,
    fontSize: 10,
    color: '#333333',
    paddingHorizontal: 4,
  },
  
  guestHeaderCell: {
    flex: 1,
    fontSize: 10,
    fontWeight: 'bold',
    color: '#003663',
    paddingHorizontal: 4,
    backgroundColor: '#f8f9fa',
    paddingVertical: 5,
  },
  
  // Terms section
  termsSection: {
    marginTop: 18,
    padding: 12,
    border: '1px solid #003663',
    breakInside: 'avoid',
    orphans: 4,
    widows: 4,
  },
  
  termsTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#003663',
    marginBottom: 8,
    breakAfter: 'avoid',
  },
  
  termsList: {
    fontSize: 8,
    color: '#333333',
    lineHeight: 1.4,
    breakInside: 'avoid',
  },
  
  termItem: {
    marginBottom: 4,
    breakInside: 'avoid',
  },
  
  // Footer section
  footerSection: {
    marginTop: 30,
    paddingTop: 20,
    borderTop: '1px solid #003663',
    breakInside: 'avoid',
    orphans: 6,
    widows: 6,
  },
  
  footerCompany: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#003663',
    marginBottom: 8,
    textAlign: 'center',
  },
  
  footerContactInfo: {
    fontSize: 9,
    color: '#666666',
    textAlign: 'center',
    lineHeight: 1.5,
    marginBottom: 4,
  },
  
  footerDocument: {
    fontSize: 8,
    color: '#999999',
    textAlign: 'center',
    fontStyle: 'italic',
    marginTop: 15,
  },
});

const HotelVoucherNew = ({ reservationData }) => {
  const { t } = useTranslation();
  
  if (!reservationData) {
    return null;
  }

  const { reservationInfo, travellers, services } = reservationData;
  const mainService = services?.find(s => s.isMainService) || services?.[0];
  const hotelDetail = mainService?.serviceDetails?.hotelDetail;
  
  // Base64 data URI (embedded) so no external fetch is required
  const logoSrc = FluturoLireLogoNewBase64;
  
  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    // Fallback: if invalid date, just return the original string
    if (isNaN(date.getTime())) return dateString.toString();
    return date.toLocaleDateString('en-GB', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Format currency helper
  const formatCurrency = (amount, currency) => {
    if (!amount) return '';
    return `${amount.toFixed(2)} ${currency}`;
  };

  const VoucherDocument = () => (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header Section */}
        <View style={styles.headerSection}>
          <View style={styles.companyInfo}>
            <View style={styles.companyLeft}>
              {/* Company logo */}
              <Image src={logoSrc} style={styles.logo} />
              <View style={styles.companyTextInfo}>
                <Text style={styles.companyName}>FluturoLire LLC</Text>
                <Text style={styles.companySubtitle}>Licensed Travel Agency</Text>
              </View>
            </View>
            <View style={styles.voucherRight}>
              <Text style={styles.voucherTitle}>HOTEL VOUCHER</Text>
              <Text style={styles.issueDate}>
                Issued: {new Date().toLocaleDateString('en-GB', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </Text>
              <Text style={styles.bookingNumber}>
                Booking Number: {reservationInfo?.bookingNumber || reservationInfo?.reservationNumber || 'N/A'}
              </Text>
            </View>
          </View>
        </View>

        {/* Booking Confirmation Section */}
        <View style={styles.bookingSection}>
          <Text style={styles.bookingBlockTitle}>BOOKING CONFIRMATION</Text>
          <Text style={styles.hotelName}>
            {hotelDetail?.name || mainService?.name || 'Hotel Name'}
          </Text>
          <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 4 }}>
            <Text style={{ fontSize: 12, color: '#FFD700', marginRight: 4 }}>★★★★★</Text>
            <Text style={{ fontSize: 10, color: '#666666' }}>(5 Star Hotel)</Text>
          </View>
        </View>

        {/* Reservation Information Table */}
        <View style={styles.infoTable}>
          <Text style={styles.tableTitle}>Reservation information</Text>
          
          {/* Header Row */}
          <View style={[styles.tableRow, styles.tableHeader]}>
            <Text style={[styles.tableCell, styles.tableHeaderText]}>Hotel Category:</Text>
            <Text style={[styles.tableCell, styles.tableHeaderText]}>Location:</Text>
            <Text style={[styles.tableCell, styles.tableHeaderText]}>Room Type:</Text>
            <Text style={[styles.tableCell, styles.tableHeaderText]}>Board Basis:</Text>
          </View>
          
          {/* Data Row */}
          <View style={styles.tableRow}>
            <Text style={styles.tableCell}>5 Star Hotel</Text>
            <Text style={styles.tableCell}>
              {hotelDetail?.city?.name || mainService?.arrivalCity?.name || 'N/A'}
            </Text>
            <Text style={styles.tableCell}>
              {mainService?.serviceDetails?.room || 'STANDARD ROOM'}
            </Text>
            <Text style={styles.tableCell}>
              {mainService?.serviceDetails?.board || 'ROOM ONLY'}
            </Text>
          </View>

          {/* Second Header Row */}
          <View style={[styles.tableRow, styles.tableHeader]}>
            <Text style={[styles.tableCell, styles.tableHeaderText]}>Check-in:</Text>
            <Text style={[styles.tableCell, styles.tableHeaderText]}>Check-out:</Text>
            <Text style={[styles.tableCell, styles.tableHeaderText]}>Duration:</Text>
            <Text style={[styles.tableCell, styles.tableHeaderText]}>Total Price:</Text>
          </View>
          
          {/* Second Data Row */}
          <View style={styles.tableRow}>
            <Text style={styles.tableCell}>
              {formatDate(reservationInfo?.beginDate)}
            </Text>
            <Text style={styles.tableCell}>
              {formatDate(reservationInfo?.endDate)}
            </Text>
            <Text style={styles.tableCell}>
              {mainService?.serviceDetails?.night || 0} nights
            </Text>
            <Text style={styles.tableCellBold}>
              {formatCurrency(
                reservationInfo?.totalPrice?.amount,
                reservationInfo?.totalPrice?.currency
              )}
            </Text>
          </View>
        </View>

        {/* Guest Information */}
        <View style={styles.guestSection}>
          <Text style={styles.tableTitle}>Guest information</Text>
          
          {/* Guest Data */}
          {travellers?.map((traveller, index) => (
            <View key={index}>
              {/* Main Guest Info */}
              <View style={styles.guestRow}>
                <Text style={[styles.guestHeaderCell, { flex: 0.3 }]}>Name:</Text>
                <Text style={[styles.guestCell, { flex: 0.7 }]}>
                  {traveller.title === 1 ? 'Mr.' : traveller.title === 3 ? 'Mrs.' : 'Ms.'} {traveller.name} {traveller.surname}
                </Text>
              </View>
              
              <View style={styles.guestRow}>
                <Text style={[styles.guestHeaderCell, { flex: 0.3 }]}>Nationality:</Text>
                <Text style={[styles.guestCell, { flex: 0.7 }]}>{traveller.nationality?.name || 'N/A'}</Text>
              </View>
              
              <View style={styles.guestRow}>
                <Text style={[styles.guestHeaderCell, { flex: 0.3 }]}>Date of Birth:</Text>
                <Text style={[styles.guestCell, { flex: 0.7 }]}>
                  {formatDate(traveller.birthDate)}
                </Text>
              </View>
              
              {traveller.isLeader && (
                <>
                  <View style={styles.guestRow}>
                    <Text style={[styles.guestHeaderCell, { flex: 0.3 }]}>Contact:</Text>
                    <Text style={[styles.guestCell, { flex: 0.7 }]}>
                      {traveller.address?.contactPhone?.countryCode || ''} {traveller.address?.contactPhone?.phoneNumber || 'N/A'}
                    </Text>
                  </View>
                  
                  <View style={styles.guestRow}>
                    <Text style={[styles.guestHeaderCell, { flex: 0.3 }]}>Email:</Text>
                    <Text style={[styles.guestCell, { flex: 0.7 }]}>{traveller.address?.email || 'N/A'}</Text>
                  </View>
                </>
              )}
              
              {index < travellers.length - 1 && (
                <View style={{ marginVertical: 10, borderBottomWidth: 1, borderBottomColor: '#eee' }} />
              )}
            </View>
          ))}
        </View>

        {/* Payment Information */}
        <View style={styles.infoTable}>
          <Text style={styles.tableTitle}>Payment Information</Text>
          
          <View style={styles.tableRow}>
            <Text style={[styles.tableCell, { flex: 0.3, fontWeight: 'bold' }]}>Total Amount:</Text>
            <Text style={[styles.tableCell, { flex: 0.7 }]}>
              {formatCurrency(
                reservationInfo?.totalPrice?.amount,
                reservationInfo?.totalPrice?.currency
              )}
            </Text>
          </View>
          
          <View style={styles.tableRow}>
            <Text style={[styles.tableCell, { flex: 0.3, fontWeight: 'bold' }]}>Payment Status:</Text>
            <Text style={[styles.tableCell, { flex: 0.7 }]}>
              {reservationInfo?.paymentStatus === 2 ? 'Paid' : 'Pending'}
            </Text>
          </View>
          
          <View style={styles.tableRow}>
            <Text style={[styles.tableCell, { flex: 0.3, fontWeight: 'bold' }]}>Transaction Date:</Text>
            <Text style={[styles.tableCell, { flex: 0.7 }]}>
              {formatDate(reservationInfo?.createDate)}
            </Text>
          </View>
        </View>

        {/* Cancellation Policy */}
        {mainService?.cancellationPolicies?.length > 0 && (
          <View style={styles.infoTable}>
            <Text style={styles.tableTitle}>Cancellation Policy</Text>
            
            {mainService.cancellationPolicies.map((policy, index) => (
              <React.Fragment key={index}>
                <View style={styles.tableRow}>
                  <Text style={[styles.tableCell, { flex: 0.3, fontWeight: 'bold' }]}>Due Date:</Text>
                  <Text style={[styles.tableCell, { flex: 0.7 }]}>
                    {formatDate(policy.dueDate)}
                  </Text>
                </View>
                
                <View style={styles.tableRow}>
                  <Text style={[styles.tableCell, { flex: 0.3, fontWeight: 'bold' }]}>Penalty Amount:</Text>
                  <Text style={[styles.tableCell, { flex: 0.7 }]}>
                    {formatCurrency(policy.price?.amount, policy.price?.currency)}
                  </Text>
                </View>
              </React.Fragment>
            ))}
          </View>
        )}

        {/* Terms and Conditions */}
        <View style={styles.termsSection}>
          <Text style={styles.termsTitle}>Important Terms & Conditions</Text>
          <View style={styles.termsList}>
            <Text style={styles.termItem}>
              -Please present this voucher at hotel check-in along with valid ID/passport
            </Text>
            <Text style={styles.termItem}>
              -Check-in time is typically 3:00 PM, check-out time is 11:00 AM
            </Text>
            <Text style={styles.termItem}>
              -Any additional services, extras, or city taxes must be paid directly to the hotel
            </Text>
            <Text style={styles.termItem}>
              -Room allocation is subject to availability at the time of check-in
            </Text>
            <Text style={styles.termItem}>
              -For any changes or cancellations, please contact FluturoLire customer service immediately
            </Text>
            <Text style={styles.termItem}>
              -This voucher is non-transferable and must be presented by the lead guest
            </Text>
            <Text style={styles.termItem}>
              -FluturoLire is not responsible for any services not provided by the hotel
            </Text>
            <Text style={styles.termItem}>
              -Please verify all details on this voucher and report any discrepancies immediately
            </Text>
            <Text style={styles.termItem}>
              -Keep this voucher safe during your travel as it serves as proof of your reservation
            </Text>
            <Text style={styles.termItem}>
              -Late check-out may incur additional charges payable directly to the hotel
            </Text>
          </View>
        </View>

        {/* Footer Section */}
        <View style={styles.footerSection}>
          <Text style={styles.footerCompany}>FluturoLire LLC - Your Trusted Travel Partner</Text>
          
          <Text style={styles.footerContactInfo}>
            24/7 Emergency Support: +383 49 25 30 35
          </Text>
          <Text style={styles.footerContactInfo}>
            Email Support: support@fluturolire.com
          </Text>
          <Text style={styles.footerContactInfo}>
            Website: www.fluturolire.com
          </Text>
          <Text style={styles.footerContactInfo}>
            Address: Bedri Berisha Nr.51 H4, Prishtina, Kosovo
          </Text>
          
          <Text style={styles.footerDocument}>
            This voucher was generated on {new Date().toLocaleDateString('en-GB', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })} at {new Date().toLocaleTimeString('en-GB', {
              hour: '2-digit',
              minute: '2-digit'
            })} • Document ID: {reservationInfo?.bookingNumber || 'RC' + Date.now().toString().slice(-6)}-{new Date().getFullYear()}{String(new Date().getMonth() + 1).padStart(2, '0')}{String(new Date().getDate()).padStart(2, '0')}
          </Text>
        </View>
      </Page>
    </Document>
  );

  return (
    <div>
      <PDFDownloadLink
        document={<VoucherDocument />}
        fileName={`hotel-voucher-${reservationInfo?.bookingNumber || 'booking'}-${Date.now()}.pdf`}
        style={{
          textDecoration: 'none',
          padding: '12px 24px',
          backgroundColor: '#003663',
          color: 'white',
          borderRadius: '8px',
          fontWeight: 'bold',
          display: 'inline-block',
          marginTop: '20px'
        }}
      >
        {({ blob, url, loading, error }) =>
          loading ? 'Generating PDF...' : 'Download Hotel Voucher'
        }
      </PDFDownloadLink>
    </div>
  );
};

export default HotelVoucherNew; 